import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { Family, FamilyListResponse, FamilyCreate, FamilyUpdate } from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';

const PAGE_SIZE = 10;

interface UseFamiliesResult {
  families: Family[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchFamilies: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createFamily: (data: FamilyCreate) => Promise<boolean>;
  updateFamily: (id: number, data: FamilyUpdate) => Promise<boolean>;
  deleteFamily: (id: number) => Promise<boolean>;
}

export const useFamilies = (): UseFamiliesResult => {
  const [families, setFamilies] = useState<Family[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { whitelabelConfig } = useWhitelabelContext();

  const getEntityTitle = (defaultTitle: string, singular?: boolean) => 
    (singular && whitelabelConfig.entities?.['families'].title_singular) || whitelabelConfig.entities?.['families'].title || defaultTitle;

  const fetchFamilies = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<FamilyListResponse>(
        `/families?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setFamilies(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch ${getEntityTitle('families')}: ${err.message}`);
      } else {
        setError(`An unknown error occurred while fetching ${getEntityTitle('families')}.`);
      }
      setFamilies([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchFamilies(currentPage);
  }, [currentPage, fetchFamilies]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createFamily = async (data: FamilyCreate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Family>('/families', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchFamilies(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create family: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateFamily = async (id: number, data: FamilyUpdate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Family>(`/families/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchFamilies(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update family: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteFamily = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete ${getEntityTitle('Family', true)} ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/families/${id}`, { method: 'DELETE' });
      const pageToFetch = families.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchFamilies(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete family: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    families,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchFamilies,
    handlePageChange,
    createFamily,
    updateFamily,
    deleteFamily,
  };
};